import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal } from 'antd';
import { Annonce } from '../../models/AnnonceModel';
import { sendMail } from '../../services/Mail_service';
import { incrementAnnonceReminderCount } from '../../services/Annonce_service';
import Swal from 'sweetalert2';
import { MailOutlined } from '@ant-design/icons';
import { UserCircle } from 'lucide-react';

interface EmailReminderModalProps {
    isModalOpen: boolean;
    setIsModalOpen: (isModalOpen: boolean) => void;
    currentAnnonce: Annonce | null;
    setCurrentAnnonce: (currentAnnonce: Annonce | null) => void;
    onReminderSent?: () => void;
}

const EmailReminderModal = ({
    isModalOpen,
    setIsModalOpen,
    currentAnnonce,
    setCurrentAnnonce,
    onReminderSent
}: EmailReminderModalProps) => {

  
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [htmlMessage, setHtmlMessage] = useState("");

  // Générer le lien de paiement au bon format
  const generatePaymentLink = () => {
    if (!currentAnnonce?.id) return 'Non disponible';
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5200';
    return `${baseUrl}/stripe/payment-session/generatelink/${currentAnnonce.id}`;
  };

useEffect(() => {
  if (currentAnnonce) {
    const paymentLink = generatePaymentLink();


    const textMessage = `
    Bonjour ${currentAnnonce?.Client?.nom} ${currentAnnonce?.Client?.prenom},

    Votre conseil ${currentAnnonce?.Client?.nom} ${currentAnnonce?.Client?.prenom} nous a transmis la demande de publication de votre annonce légale, réalisée en votre faveur.

    Veuillez trouver ci-dessous les éléments relatifs à cette parution :

    - Support de presse : ${currentAnnonce?.nomJal || '------'}
    - Type d’annonce : ${currentAnnonce?.type_annonce || '--------'}
    - Montant à régler : ${
      currentAnnonce?.montantTTC
        ? currentAnnonce.montantTTC + ' €'
        : '------- €'
    }
    - Lien de paiement : ${paymentLink}

    Le règlement de cette somme permettra la validation immédiate de la publication et l’émission de l’attestation officielle de parution, destinée à vos conseils pour la finalisation du dossier.

    Nous vous remercions par avance pour votre réactivité et pour la confiance accordée à notre service.

    Très cordialement,
    L’équipe ALO
    `;

    const html = `
<div>
<p>Bonjour,</p>
<p>Votre  conseil ${currentAnnonce?.Client?.nom} ${currentAnnonce?.Client?.prenom} nous a transmis la demande de publication de votre annonce légale, réalisée en votre faveur.</p>
    <p>Veuillez trouver ci-dessous les éléments relatifs à cette parution :</p>
  <ul>
    <li><strong>Type d'annonce :</strong> ${currentAnnonce?.type_annonce}</li>
    <li><strong>Support de presse :</strong> ${currentAnnonce?.nomJal || 'Non spécifié'}</li>
    <li><strong>Montant à facturer :</strong> ${currentAnnonce?.montantTTC} €</li>
    <li><strong>Lien de paiement :</strong> <a href="${paymentLink}">Cliquez ici</a></li>
  </ul>
    <p>Le règlement de cette somme permettra la validation immédiate de la publication et l’émission de l’attestation officielle de parution, destinée à votre conseil pour finalisation du dossier.</p>

    <p>Nous vous remercions par avance pour votre réactivité et pour la confiance accordée à notre service.</p>

    <p>Très cordialement,<br/>
    L’équipe ALO<br/>
    Annonce Légale Officielle – <a href="http://ALO-FR.fr">ALO-Annonce Légale Officielle vos annonces en quelques clics</a><br/>
    (Support agréé pour la diffusion d’annonces légales et judiciaires)</p>
</div>
`;

setHtmlMessage(html);


    form.setFieldsValue({
      email: currentAnnonce?.Client?.email,
      nom: currentAnnonce?.Client?.nom,
      prenom: currentAnnonce?.Client?.prenom,
      object: `Relance Facturation de la création d'annonce - ${currentAnnonce?.type_annonce}`,
      message: textMessage, 
      htmlMessage 
    });
  }
}, [currentAnnonce, form]);


  const handleOk = async( values: any ) => {

    setConfirmLoading(true);

     const payload = {
        name: values.nom + " " + values.prenom,
        email: values.email,
        object: `Relance Facturation de la création d'annonce - ${currentAnnonce?.type_annonce}`,
        sender: undefined,
        message: htmlMessage,
        bcc: [],
        allowUnsubscribe: false,
    }

    // console.log( payload )
    await sendMail( payload )
    
    // Increment reminder count
    if (currentAnnonce?.id) {
      console.log('Incrementing reminder for annonce ID:', currentAnnonce.id);
      try {
        await incrementAnnonceReminderCount(currentAnnonce.id);
        console.log('Reminder incremented successfully');
      } catch (error) {
        console.error('Failed to increment reminder:', error);
      }
    }
    
    if (onReminderSent) {
      onReminderSent();
    }
    
    setIsModalOpen(false);
    setCurrentAnnonce( null )
    
        Swal.fire({
          icon: "success",
          title: "Relance de paiement",
          text: "Le message de relance a été envoyé avec succès."
        });

    setConfirmLoading(false);
    form.resetFields()
  };

  const handleCancel = () => {
    form.resetFields()
    setIsModalOpen(false);
  };

  return (
    <>
      <Modal
        title=" Relance de paiement"
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isModalOpen}
        onCancel={handleCancel}
        confirmLoading={confirmLoading}
        footer = { false }
      >
        
        <Form
        form={form}
        layout="vertical"
        onFinish={handleOk}
      >
        <Form.Item
          label="Email du client"
          name="email"
          rules={[
            { required: true, message: "L'email est requis" },
            { type: 'email', message: 'Email invalide' },
          ]}
        >
          <Input prefix={<MailOutlined />}
            placeholder="Email du client" />
        </Form.Item>

        <Form.Item
          label="Nom du client"
          name="nom"
          rules={[
            { required: true, message: "Le nom est requis" },
          ]}
        >
          <Input prefix={<UserCircle />}
            placeholder="nom du client" />
        </Form.Item>

        <Form.Item
          label="Prenom du client"
          name="prenom"
          rules={[
            { required: true, message: "Le prenom est requis" },
          ]}
        >
          <Input prefix={<UserCircle />}
            placeholder="prenom du client" />
        </Form.Item>

        <Form.Item
          label="Objet de l'email"
          name="object"
          rules={[{ required: true, message: "L'objet est requis" }]}
        >
          <Input placeholder="Objet de l'email" />
        </Form.Item>

        <Form.Item
          label="Message"
          name="message"
          rules={[{ required: true, message: 'Le message est requis' }]}
        >
          <Input.TextArea rows={10} placeholder="Votre message..." readOnly />
        </Form.Item>

        <Form.Item className="mb-0">
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>Annuler</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={ confirmLoading }
              style={{ backgroundColor: '#2A919D' }}
            >
              Envoyer la facturation
            </Button>
          </div>
        </Form.Item>
      </Form>
        
      </Modal>
    </>
  );
};

export default EmailReminderModal;